import { Component, PLATFORM_ID, inject, input, OnInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth.service';
import { LoaderServices } from '../../../shared/services/loader.service';
import { UserApiService } from '../../../shared/services/user-api.service';
import { SubscriptionService } from '../../../shared/services/subscription.service';
import { AnalyticsUtils } from '../../../shared/services/analytics-utils';
import { SharedService } from '../../../shared/services/shared.service';
import { ReferrerTrackingService } from '../../../shared/services/referrer-tracking.service';
import { ReturnUrlService } from '../../../shared/services/return-url.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserOnboardingServeComponent } from '../../../shared/components/user-onboarding-serve/user-onboarding-serve.component';

declare var AppleID: any;

@Component({
   selector: 'app-apple-login',
   templateUrl: './apple-login.component.html',
   styleUrls: ['./apple-login.component.scss'],
   host: {
      '[class.btn-styling]': 'isAppleBtnInsidePopup()'
   }
})
export class AppleLoginComponent implements OnInit {
   // Inputs using signal-based input() API
   buttonText = input<string>('Login via Apple');
   clickEvent = input<string>('');
   tapEvent = input<string>('');
   completedEvent = input<string>('');
   failEvent = input<string>('');
   isAppleBtnInsidePopup = input<boolean>(false);

   // Dependency Injections
   private readonly platformId = inject(PLATFORM_ID);
   private readonly router = inject(Router);
   public readonly auth = inject(AuthService); // Kept public to match original footprint
   private readonly loader = inject(LoaderServices);
   public readonly userApiService = inject(UserApiService);
   public readonly subscriptionService = inject(SubscriptionService);
   private readonly analyticsUtils = inject(AnalyticsUtils);
   private readonly sharedService = inject(SharedService);
   private readonly modalService = inject(NgbModal);
   private readonly referrerTrackingService = inject(ReferrerTrackingService);
   private readonly returnUrlService = inject(ReturnUrlService);

   private readonly isBrowser = isPlatformBrowser(this.platformId);
   private redirectURL: string | null = null;

   constructor() {
      if (this.isBrowser) {
         this.redirectURL = localStorage.getItem('redirectUrl');
      }
   }

   ngOnInit(): void {
      if (this.isBrowser) {
         this.returnUrlService.captureReturnUrl();
      }
   }

   parseJwt(token: any) {
      if (!this.isBrowser || !token) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
         atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
      );
      return JSON.parse(jsonPayload);
   }

   async handleClick() {
      try {
         if (!this.isBrowser) {
            console.warn('Apple Sign-in is not available in SSR context');
            return;
         }

         this.analyticsUtils.SOCIAL_AUTH_BTN_TAP(this.tapEvent());
         AppleID.auth.init({
            clientId: 'com.musaffa.web.dev',
            scope: 'name email',
            redirectURI: `${window.location.origin}/authentication/login`,
            state: 'init',
            nonce: 'test',
            usePopup: true,
         });
         const data = await AppleID.auth.signIn();

         this.loader.show();

         const resp: any = await this.userApiService.appleLogin({
            token: data.authorization.id_token
         });

         if (resp.status) {
            this.referrerTrackingService.clearReferrerData();
            const token = resp.data;
            const isNew = resp.is_new;
            await this.auth.setToken(token);

            if (this.isAppleBtnInsidePopup()) {
               this.auth.closeAuthPopup();
            }

            await this.subscriptionService.setUserSubscriptionInfo();
            const subInfo = SubscriptionService.subscriptionData.getValue();
            const userData: any = await this.userApiService.fetchUserDetailsAndSave();

            if (isNew) {
               this.analyticsUtils.SignupFlow_SaveUser_Completed('apple');
               this.sharedService.afterSignUpActions(userData.email);
               // this.modalService.open(UserOnboardingServeComponent, {
               //    centered: true,
               //    scrollable: true,
               //    size: 'xl',
               //    modalDialogClass: 'paywall-popup-modal',
               // });
            }

            this.analyticsUtils.SOCIAL_AUTH_COMPLETED(
               this.completedEvent(),
               userData.email,
               resp.is_new,
               AnalyticsUtils.METHOD_APPLE
            );

            if (!userData?.sign_up_completed) {
               if (this.isAppleBtnInsidePopup()) {
                  this.auth.closeAuthPopup();
               }
               this.router.navigate(['/authentication/activate']);
               return;
            }

            if (this.isAppleBtnInsidePopup()) {
               window.location.reload();
            } else {
               this.auth.setLoggedIn();
               const returnUrl = this.returnUrlService.getReturnUrl();
               if (returnUrl) {
                  this.returnUrlService.redirectAfterAuth('/', token);
               } else if (this.redirectURL) {
                  this.router.navigateByUrl(this.redirectURL);
               } else {
                  this.router.navigateByUrl('/');
               }
            }
         }
      } catch (error: any) {
         this.analyticsUtils.SOCIAL_AUTH_FAILED(this.failEvent(), '', '', error?.message || 'Unknown error');
      } finally {
         this.loader.hide();
      }
   }
}
